import { DEFAULT_MEETING_MODE, type MeetingMode } from '@shared/types/AudioNoteSettings'

const MEETING_SETTINGS_BY_MODE = {
  autopilot: {
    meetingDetection: true,
    meetingNotifications: true,
    autoTranscribeMeetings: true,
  },
  notify: {
    meetingDetection: true,
    meetingNotifications: true,
    autoTranscribeMeetings: false,
  },
  silent: {
    meetingDetection: false,
    meetingNotifications: false,
    autoTranscribeMeetings: false,
  },
} satisfies Record<
  MeetingMode,
  {
    meetingDetection: boolean
    meetingNotifications: boolean
    autoTranscribeMeetings: boolean
  }
>

export const resolveMeetingMode = (mode: unknown): MeetingMode => {
  if (mode === 'autopilot' || mode === 'notify' || mode === 'silent') {
    return mode
  }

  return DEFAULT_MEETING_MODE
}

export const getMeetingSettingsForMode = (mode: MeetingMode) => {
  return MEETING_SETTINGS_BY_MODE[mode]
}

/**
 * Whether joining is enough to get a transcript, with nothing left to confirm.
 * Every other argument is a way the auto-start never happens: a link-less event
 * surfaces no meeting window to detect, incognito suppresses the start (the
 * same gate as shouldAutoTranscribeMeetings), and out of hours the billing gate
 * refuses it. Say this only when it is true, or the toast promises a transcript
 * the user never gets.
 */
export const willTranscribeOnJoin = ({
  mode,
  hasJoinLink,
  incognito,
  outOfHours,
}: {
  mode: MeetingMode
  hasJoinLink: boolean
  incognito: boolean
  outOfHours: boolean
}): boolean => getMeetingSettingsForMode(mode).autoTranscribeMeetings && hasJoinLink && !incognito && !outOfHours

/**
 * Whether the join click is what buys the transcript, and so belongs in the CTA.
 * Autopilot records either way and silent never detects the meeting at all, so
 * notify is the only mode whose transcript hinges on the click. The join still
 * works incognito and out of hours, but the armed join refuses to record and the
 * billing gate refuses the start, so the label drops the promise rather than
 * naming a transcript the click never produces.
 */
export const joinDecidesTranscription = ({
  mode,
  incognito,
  outOfHours,
}: {
  mode: MeetingMode
  incognito: boolean
  outOfHours: boolean
}): boolean => {
  const { meetingDetection, autoTranscribeMeetings } = getMeetingSettingsForMode(mode)
  return meetingDetection && !autoTranscribeMeetings && !incognito && !outOfHours
}
